import { config } from '@/config/index';

import { authMiddleware } from '@/middlewares/auth';
import TokenManager from '@/middlewares/auth/TokenManager';

import { TokensI } from '../../contexts/Auth/authTokens';
import { emit } from '../../eventBus';
import { RefreshTokenUpdateError } from '../core/errors';
import { request } from '../core/request';

const checkIsUserInBetaMode = (): boolean => {
  return window.sessionStorage.getItem(config.auth.betaUserID) === 'true';
};

const getAuthDemoEndpoint = (path: string): string => {
  const isUserInBetaMode = checkIsUserInBetaMode();
  const oauthPath = `/oauth${path}`;

  if (isUserInBetaMode) {
    return `/oauth/demo${path}`;
  }

  return oauthPath;
};

const getAuthorizedBetaUserEndpoint = (path: string): string => {
  const isUserInBetaMode = checkIsUserInBetaMode();
  const oauthPath = `/oauth${path}`;

  if (isUserInBetaMode) {
    return `/oauth/demo${path}`;
  }

  return oauthPath;
};

interface IRequestLogout {
  success: boolean;
}

const setLastRefreshTokensTime = (): void => localStorage.setItem('lastTokensRefresh', Date.now().toString());

export const singInWithPassword = async (email: string, password: string): Promise<TokensI> => {
  const headers = new Headers();

  const response = await request(
    getAuthDemoEndpoint('/token'),
    'POST',
    {
      password,
      email,
    },
    headers,
    { credentials: 'include' },
  );

  if (!response.ok) {
    throw new Error(await response.text());
  }

  setLastRefreshTokensTime();
  const tokens: TokensI = await response.json();
  return tokens;
};

export const requestLogout = async (): Promise<IRequestLogout> => {
  const headers = new Headers();

  const tokenManager = TokenManager.getInstance();
  const accessToken = tokenManager.getAccessToken();
  headers.set('authorization', `Bearer ${accessToken}`);

  const response = await authMiddleware(
    (resultHeaders) =>
      request(getAuthorizedBetaUserEndpoint('/logout'), 'POST', null, resultHeaders, { credentials: 'include' }),
    headers,
  );

  if (!response.ok) {
    throw new Error(await response.text());
  }

  if (checkIsUserInBetaMode()) {
    window.sessionStorage.removeItem(config.auth.betaUserID);
  }

  return { success: true };
};

interface RefreshTokensProps {
  authChecksum?: Nullable<string>;
  refreshToken?: Nullable<string>;
  withLogout?: boolean;
}

export const refreshTokens = async (props: Nullable<RefreshTokensProps>): Promise<TokensI> => {
  const { authChecksum: auth_checksum, withLogout = true } = props ?? {};

  const headers = new Headers();
  const data: Record<string, string> = {};
  !!auth_checksum && (data.auth_checksum = auth_checksum);

  const response = await request(
    getAuthorizedBetaUserEndpoint('/refresh'),
    'POST',
    Object.keys(data).length ? data : null,
    headers,
    {
      credentials: 'include',
    },
  );

  if (!response.ok) {
    if (withLogout) {
      const tokenManager = TokenManager.getInstance();
      const accessToken = tokenManager.getAccessToken();

      if (accessToken) {
        emit({ action: 'session_expired', url: window.location.pathname + window.location.search });
      }

      emit({ action: 'logout' });
    }
    throw new RefreshTokenUpdateError();
  }

  setLastRefreshTokensTime();
  const tokens: TokensI = await response.json();

  return tokens;
};
